import { Controller } from '@hotwired/stimulus'

// Guidance on usage from original PR: https://github.com/CampusESP/www/pull/7603
/*
# How the stimulus controller works

To make a generic accessible error handler, I used a stimulus controller that takes in
all the input fields you want to report errors for and the list of errors and will create
properly code the error text. The stimulus controller goes through every error and does the following:
- It inserts the error text into the view
- It then links the input to the error text using the `aria-describedby` attribute to point
  the input field to the error text. The value of 'aria-describedby` for the input field
  will be the id of the error field.
- It makes `aria-invalid=true` for the input field itself.
- Finally, the error text will be `role=alert` so the error text is read aloud when the screen is loaded.

# Using the stimulus controller

The format for the errors to give to the stimulus controller needs to be in a hash,
where the key is the method for the input field it is reporting for and the value is a
list of all the error for that input method.

ex. ```
@errors = {'email': ['Email should look like an email address.'],
'password': ['Password is too short (minimum 8).', 'Password must have at least one number.',
'Password must have upper and lowercase letters.'],
'password_confirmation': ['Password confirmation and password do not match.']}
```

To apply the stimulus controller, add the following to code to the form:

`data: { controller: 'form-error', 'form-error-errors-value': @errors }`

Then add this to the input field (the text field, checkbox, etc.):

`data: { 'method-name': METHOD_NAME, 'form-error-target': 'inputField' }.`

Finally add this code to where you want the error message to display:

`.form-error-text{ role: 'alert', id: 'METHOD_NAME-error', data: { 'form-error-target': 'errorField' } }`

# Accessing errors

The hard part is finding the errors that the form generates-
each form we created already kinda does it in a different way.

If a form and its urls are properly coded, then you should be able to access its errors in the hash
format seen above with `current_user.errors.as_json(full_messages: true)`.
*/
export default class extends Controller {
  static targets = ['inputField', 'errorField']
  static values = { errors: { type: Object, default: {} } }

  connect() {
    Object.entries(this.errorsValue).forEach(([fieldName, errorMessage]) => {
      this.errorFieldTargets.forEach((errorField) => {
        if (errorField.id === `${fieldName}-error`) {
          errorField.innerHTML = Array.isArray(errorMessage)
            ? errorMessage.join(' <br> ')
            : errorMessage
        }
      })
      this.inputFieldTargets.forEach((inputField) => {
        if (inputField.getAttribute('data-method-name') === fieldName) {
          // if this inputs already has a value for aria-describedby, just add onto the value
          if (inputField.hasAttribute('aria-describedby')) {
            inputField.setAttribute(
              'aria-describedby',
              `${inputField.getAttribute('aria-describedby')} ${fieldName}-error`,
            )
          } else {
            inputField.setAttribute('aria-describedby', `${fieldName}-error`)
          }
          inputField.setAttribute('aria-invalid', 'true')
        }
      })
    })
  }
}
